/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';
import { isRightToLeft } from 'js/lib/language';

import { breakpoints, useTheme } from '@coursera/cds-core';

import _t from 'i18n!nls/general-video-player';

type Props = {
  src: string;
  onPlayButtonClicked: () => void;
};

function VideoPlaceholder({ src, onPlayButtonClicked }: Props) {
  const theme = useTheme();
  const isRTL = isRightToLeft(_t.getLocale());
  const styles = {
    root: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    `,

    videoMask: css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgb(0 0 0 / 50%);
    `,
    vjsBigPlayBbutton: css`
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 3em;
      height: 3em;
      font-size: 3em;
      border-radius: 50%;
      line-height: 1.5em;
      cursor: pointer;
      border: 0.06666em solid #fff; /* stylelint-disable-line number-max-precision */
      background-color: rgb(43 51 63 / 70%);
      transition: all 0.4s;

      ${breakpoints.down('xs')} {
        width: 2.5em;
        height: 2.5em;
        font-size: 2.5em;
        line-height: 1.25em;
      }
    `,
    vjsControlText: css`
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    `,

    // centered triangle icon looks offsetting to the left, so we add a margin-left
    // however for rtl languages, we need to add a margin-right since the triangle is NOT flipped
    centerSpan: css`
      ${isRTL ? 'margin-right' : 'margin-left'}: 0.2em;

      color: var(--cds-color-white-0);
    `,

    videoPlaceholder: css`
      width: 100%;
      max-height: 100%;
    `,
  };

  return (
    <div css={styles.root}>
      <ImgixWithPrioritizedLoading alt="video-placeholder" src={src} css={styles.videoPlaceholder} />
      <div css={styles.videoMask} />
      <button
        css={styles.vjsBigPlayBbutton}
        type="button"
        aria-live="polite"
        onClick={onPlayButtonClicked}
        data-e2e="video-play-button"
      >
        <span className="cif-play" css={styles.centerSpan} />
        <span css={styles.vjsControlText}>{_t('Play Video')}</span>
      </button>
    </div>
  );
}

export default VideoPlaceholder;
